/**
 * DesktopAdminView -- Centro de control Nexo Desktop.
 *
 * ⚠ El registro de clientes se hace desde cada Negocio → pestaña Suscripción.
 *
 * Tabs:
 *   1. Estado global  – tabla de todos los negocios con su estado Desktop
 *   2. Pendientes     – clientes sin negocio vinculado (huérfanos), para enlazarlos o eliminar
 *   3. Versiones      – versión estable actual y publicar nuevas versiones
 *
 * @module presentation/views/DesktopAdminView
 */

import { LicenseApiClient } from '../../infrastructure/api/LicenseApiClient.js';
import { VersionApiClient }  from '../../infrastructure/api/VersionApiClient.js';
import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
import { ApiBase } from '../../infrastructure/api/ApiBase.js';
import { filtrarPorVista, listaDe, equiposPorNegocio, totalesDeEquipos } from '../../application/negocios/archivoNegocios.js';
import { renderFlota } from './desktop/FlotaTab.js';
import { confirmar } from '../shared/nxDialogo.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const APP = 'nexo_desktop';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!isRealDate(iso)) return '—';
  const d = new Date(typeof iso === 'string' && iso.length === 10 ? iso + 'T12:00:00' : iso);
  return formatearFecha(d, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Ignora min-dates de SQL (0001-01-01) que llegan como fechas falsas. */
function isRealDate(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && d.getFullYear() >= 2000;
}

function expiryChip(dateIso, fromBiz = false) {
  if (!isRealDate(dateIso) && fromBiz) {
    return `<span class="da-badge da-badge-ok">Desde suscripción del negocio</span>`;
  }
  if (!isRealDate(dateIso)) {
    return `<span class="da-badge" style="background:var(--bg-tertiary);color:var(--text-muted)">Sin fecha</span>`;
  }
  const d    = new Date(dateIso);
  const days = Math.ceil((d - Date.now()) / 86400000);
  // Periodo de gracia oficial: 5 dias desde end_date (Desktop sigue funcionando sin bloqueo).
  // Distinguimos visualmente vencida-en-gracia (amarillo) vs vencida-fuera-de-gracia (rojo critico).
  if (days < 0) {
    const daysOver = Math.abs(days);
    if (daysOver <= 5) {
      return `<span class="da-badge da-badge-yellow" title="Vencio el ${fmtDate(dateIso)} pero el cliente Desktop sigue operativo durante el periodo de gracia (5 dias)">Vencida ${fmtDate(dateIso)} (gracia ${5 - daysOver}d)</span>`;
    }
    return `<span class="da-badge da-badge-red" title="Vencida hace ${daysOver}d, fuera del periodo de gracia. Cliente Desktop bloqueado.">Vencida ${fmtDate(dateIso)}</span>`;
  }
  if (days < 14) return `<span class="da-badge da-badge-yellow">${fmtDate(dateIso)} (${days}d)</span>`;
  return `<span class="da-badge da-badge-ok">${fmtDate(dateIso)}</span>`;
}

/** Calcula horas desde un ISO date y retorna { hoursAgo, freshClass, freshLabel }. */
function syncFreshness(lastSyncIso) {
  if (!lastSyncIso) return { hoursAgo: Infinity, freshClass: '', freshLabel: 'Sin datos' };
  const lastSync = new Date(lastSyncIso);
  if (isNaN(lastSync.getTime())) return { hoursAgo: Infinity, freshClass: '', freshLabel: 'Sin datos' };
  const hoursAgo = Math.floor((Date.now() - lastSync) / 3600000);
  const freshClass = hoursAgo < 24 ? 'da-badge-ok' : hoursAgo < 72 ? 'da-badge-yellow' : 'da-badge-red';
  const freshLabel = hoursAgo < 1 ? 'hace <1h' : hoursAgo < 24 ? `hace ${hoursAgo}h` : `hace ${Math.floor(hoursAgo / 24)}d`;
  return { hoursAgo, freshClass, freshLabel };
}

function syncBadge(lastSyncIso) {
  const { freshClass, freshLabel } = syncFreshness(lastSyncIso);
  if (!freshClass) return `<span class="da-badge" style="background:var(--bg-tertiary);color:var(--text-muted)">${freshLabel}</span>`;
  return `<span class="da-badge ${freshClass}">${freshLabel}</span>`;
}

/**
 * Health badge compuesto: suscripcion + cliente + sync.
 * Verde: todo OK. Amarillo: warning. Rojo: critico. Gris: sin datos.
 */
function healthBadge(hasClient, expIso, lastSyncIso) {
  const now = Date.now();
  // Suscripcion
  let subStatus = 'grey'; // sin datos
  if (isRealDate(expIso)) {
    const days = Math.ceil((new Date(expIso) - now) / 86400000);
    if (days < 0) subStatus = 'red';
    else if (days < 14) subStatus = 'yellow';
    else subStatus = 'green';
  } else if (hasClient) {
    subStatus = 'green'; // desde suscripcion del negocio
  }

  // Cliente
  const clientStatus = hasClient ? 'green' : 'red';

  // Sync
  const { hoursAgo } = syncFreshness(lastSyncIso);
  let syncStatus = 'grey';
  if (hoursAgo !== Infinity) {
    syncStatus = hoursAgo < 24 ? 'green' : hoursAgo < 72 ? 'yellow' : 'red';
  }

  const statuses = [subStatus, clientStatus, syncStatus];
  if (statuses.every(s => s === 'grey')) return `<span class="da-badge" style="background:var(--bg-tertiary);color:var(--text-muted)" title="Sin datos suficientes">&#9679;</span>`;
  if (statuses.includes('red')) return `<span class="da-badge da-badge-red" title="Critico: suscripcion/cliente/sync con problemas">&#9679;</span>`;
  if (statuses.includes('yellow')) return `<span class="da-badge da-badge-yellow" title="Warning: revisar suscripcion o sync">&#9679;</span>`;
  return `<span class="da-badge da-badge-ok" title="Saludable: suscripcion activa + cliente + sync reciente">&#9679;</span>`;
}

// ─── Vista principal ──────────────────────────────────────────────────────────

export async function renderDesktopAdmin(container) {
  // Preservar tab activo entre re-renders
  const prevTab = container.querySelector('.da-tab.active')?.dataset.tab || 'estado';

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Nexo Desktop</h1>
          <p class="subtitle">Estado global de clientes &middot; Para registrar un cliente ve a <strong>Negocios &rarr; Suscripci&oacute;n</strong></p>
        </div>
      </div>
      <div style="background:var(--bg-card);border:1px solid var(--border-default);border-radius:6px;padding:10px 14px;margin-bottom:12px;display:flex;gap:12px;align-items:center;flex-wrap:wrap">
        <span style="font-size:0.875rem;color:var(--text-secondary)">Herramientas Desktop:</span>
        <a href="#/equipos" class="btn-primary btn-sm" style="font-size:0.875rem">&#128295; Cat&aacute;logo de Equipos</a>
        <a href="#/inventario" class="btn-ghost btn-sm" style="font-size:0.875rem">&#128203; Inventario</a>
        <a href="#/carrito" class="btn-ghost btn-sm" style="font-size:0.875rem">&#128722; Carrito</a>
      </div>
      <div class="da-tabs">
        <button class="da-tab${prevTab==='estado'?' active':''}" data-tab="estado">&#128200; Estado global</button>
        <button class="da-tab${prevTab==='pendientes'?' active':''}" data-tab="pendientes">&#9888;&#65039; Pendientes</button>
        <button class="da-tab${prevTab==='alertas'?' active':''}" data-tab="alertas">&#128680; Alertas</button>
        <button class="da-tab${prevTab==='versions'?' active':''}" data-tab="versions">&#128230; Versiones</button>
        <button class="da-tab${prevTab==='flota'?' active':''}" data-tab="flota">&#128187; Flota</button>
      </div>
      <div id="da-tab-estado"     class="da-tab-content"${prevTab!=='estado'     ?' style="display:none"':''}></div>
      <div id="da-tab-pendientes" class="da-tab-content"${prevTab!=='pendientes' ?' style="display:none"':''}></div>
      <div id="da-tab-alertas"    class="da-tab-content"${prevTab!=='alertas'    ?' style="display:none"':''}></div>
      <div id="da-tab-versions"   class="da-tab-content"${prevTab!=='versions'   ?' style="display:none"':''}></div>
      <div id="da-tab-flota"      class="da-tab-content"${prevTab!=='flota'      ?' style="display:none"':''}></div>
    </section>`;

  container.querySelectorAll('.da-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      container.querySelectorAll('.da-tab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      container.querySelectorAll('.da-tab-content').forEach(c => c.style.display = 'none');
      document.getElementById(`da-tab-${btn.dataset.tab}`).style.display = 'block';
    });
  });

  // Cargar datos una sola vez, distribuir entre tabs
  const [clientsRes, bizRes, machinesRes, equiposRes] = await Promise.all([
    LicenseApiClient.listClients(),
    BusinessApiClient.list(),
    LicenseApiClient.listMachines(),
    BusinessApiClient.equipos(),
  ]);

  const allClients = (() => {
    const r = clientsRes?.data?.data ?? clientsRes?.data ?? [];
    return Array.isArray(r) ? r : (r?.items ?? []);
  })();

  // NEGOCIOS-ARCHIVAR-VISTA-T1: solo los negocios que trabajan con Nexo (activos y pendientes). La API nueva ya no trae
  // los archivados sin ?status; la de antes si, por eso el panel tambien filtra.
  const businesses = filtrarPorVista(listaDe(bizRes), 'activos');
  const idsVisibles = new Set(businesses.map((b) => b.businessId || b.business_id || b.id));

  // PANEL-OPS-V3-001 T5 — license_machines (heartbeat + PC name con prioridad sobre license_clients)
  const allMachines = (() => {
    const r = machinesRes?.data?.data ?? machinesRes?.data ?? [];
    return Array.isArray(r) ? r : (r?.items ?? []);
  })();
  // Las filas de PCs de negocios archivados no cuentan (la API las apaga al archivar; las viejas pueden seguir vivas).
  const activeMachines = allMachines.filter(m => m.active !== false && idsVisibles.has(m.business_id || m.businessId));
  // LIC-UN-PC-POR-NEGOCIO-T1: un PC es un nombre de PC distinto por negocio, no una fila (52 filas eran 26 PCs).
  // Los permitidos salen de v_negocios_equipos cuando la API la publica (#22545); si no, 1.
  const equiposPublicados = equiposRes?.ok === true;
  const equipos = equiposPorNegocio(activeMachines, equiposPublicados ? listaDe(equiposRes) : []);
  const machineByBizId = {};
  for (const m of activeMachines) {
    const bId = m.business_id || m.businessId;
    if (!bId) continue;
    const existing = machineByBizId[bId];
    const lastSeen = m.last_seen || m.lastSeen;
    // Prioriza la ultima maquina con last_seen mas reciente (1 por negocio en la tabla)
    if (!existing) {
      machineByBizId[bId] = m;
    } else {
      const existingLast = existing.last_seen || existing.lastSeen;
      if (lastSeen && (!existingLast || new Date(lastSeen) > new Date(existingLast))) {
        machineByBizId[bId] = m;
      }
    }
  }

  // Solo clientes activos
  const clients = allClients.filter(c => c.active !== false);

  // Índice de clientes vinculados por business_id
  const clientByBizId   = {};
  // Índice secundario por empresa (fallback para clientes aún no vinculados en BD)
  const clientByEmpresa = {};
  const orphans = [];
  for (const c of clients) {
    const bId = c.business_id || c.businessId;
    if (bId) clientByBizId[bId] = c;
    else orphans.push(c);
    if (c.empresa) clientByEmpresa[c.empresa] = c;
  }

  await renderEstado(container.querySelector('#da-tab-estado'), businesses, clientByBizId, clientByEmpresa, machineByBizId, activeMachines, equipos, equiposPublicados);
  renderPendientes(container.querySelector('#da-tab-pendientes'), orphans, businesses, container);
  await renderAlertas(container.querySelector('#da-tab-alertas'), businesses, clientByBizId, clientByEmpresa, activeMachines);
  await renderVersions(container.querySelector('#da-tab-versions'));
  // FLOTA-PANEL-VISTA-T1 (SI de Ares #32349): un renglon por PC con su version, cuantas va atras y sus banderas.
  await renderFlota(container.querySelector('#da-tab-flota'), businesses, activeMachines);
}

// PANEL-OPS-V3-001 T5 — Formato relativo de fecha (reusable local)
function fmtRelativeDA(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '—';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'en el futuro';
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'hace unos seg';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h`;
  const d2 = Math.floor(h / 24);
  if (d2 < 30) return `hace ${d2}d`;
  return `hace ${Math.floor(d2 / 30)}mo`;
}

// ─── Tab 1: Estado global ─────────────────────────────────────────────────────

function autoEmpresaKey(displayName) {
  return (displayName || '')
    .toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9\-]/g, '')
    .slice(0, 40);
}

async function renderEstado(el, businesses, clientByBizId, clientByEmpresa = {}, machineByBizId = {}, activeMachines = [], equipos = new Map(), equiposPublicados = false) {
  // v2.2.97: deteccion de fingerprint compartido entre negocios.
  // Si el MISMO machine_fingerprint aparece en 2+ business_id, es ambiguo saber a quien
  // pertenece el PC fisico. El admin debe revisar los pc_name locales y resolver.
  // Tambien: multiples PCs por negocio (Tennis Madrid tiene TENNIS-MADRID + MADRID-C).
  const fingerprintToBizIds = {};
  const machinesByBizId = {};
  for (const m of activeMachines) {
    const fp = m.machine_fingerprint || m.machineFingerprint;
    const bId = m.business_id || m.businessId;
    if (!bId) continue;
    if (!machinesByBizId[bId]) machinesByBizId[bId] = [];
    machinesByBizId[bId].push(m);
    if (!fp) continue;
    if (!fingerprintToBizIds[fp]) fingerprintToBizIds[fp] = new Set();
    fingerprintToBizIds[fp].add(bId);
  }
  const conflictByBizId = {};
  for (const [fp, bIds] of Object.entries(fingerprintToBizIds)) {
    if (bIds.size > 1) {
      for (const bId of bIds) {
        const otros = Array.from(bIds).filter(x => x !== bId);
        conflictByBizId[bId] = otros;
      }
    }
  }
  const conflictCount = Object.keys(conflictByBizId).length;

  const bizIdsWithClient = new Set(Object.keys(clientByBizId));

  // Fallback: un negocio está "con cliente" si business_id coincide O si autoEmpresaKey(nombre) coincide
  function getClient(b) {
    const bId  = b.businessId || b.business_id || b.id;
    const eKey = autoEmpresaKey(b.displayName || b.display_name || '');
    return clientByBizId[bId] || clientByEmpresa[eKey] || null;
  }

  const rows = businesses.filter(b => {
    const bId = b.businessId || b.business_id || b.id;
    const eKey = autoEmpresaKey(b.displayName || b.display_name || '');
    return b.licenseStatus || b.plan || bizIdsWithClient.has(bId) || !!clientByEmpresa[eKey];
  }).sort((a, b) => {
    // Orden alfabetico por nombre de negocio (case-insensitive, locale-aware)
    const na = (a.displayName || a.display_name || '').toLowerCase();
    const nb = (b.displayName || b.display_name || '').toLowerCase();
    return na.localeCompare(nb, 'es');
  });

  const registered = rows.filter(b => !!getClient(b)).length;

  // Detectar suscripciones vencidas o por vencer
  // Prioridad: effective_end_date (calculada por API desde business_subscriptions) → subscription_limit (legado)
  const now = Date.now();
  function getEffectiveExp(c) {
    return c?.effective_end_date || c?.effectiveEndDate
        || c?.subscription_limit || c?.subscriptionLimit || '';
  }
  const expired = rows.filter(b => {
    const c = getClient(b);
    const exp = getEffectiveExp(c);
    return isRealDate(exp) && new Date(exp) < now;
  });
  const expiring = rows.filter(b => {
    const c = getClient(b);
    const exp = getEffectiveExp(c);
    if (!isRealDate(exp)) return false;
    const days = Math.ceil((new Date(exp) - now) / 86400000);
    return days >= 0 && days < 14;
  });

  // Fetch sync status para cada negocio (N+1 hasta que exista endpoint batch).
  // [NEEDS-API] GET /api/sync/status/summary?businessIds=id1,id2,id3 retornaria todo en 1 request.
  // Mitigaciones aplicadas:
  //   1. Promise.allSettled (no falla si un negocio falla)
  //   2. Timeout 3s via AbortController (no bloquea la vista)
  //   3. console.warn de errores (no silencioso)
  //   4. Concurrencia limitada a 5 simultaneas (evita saturar el backend)
  const syncMap = {};
  const syncErrors = [];

  async function fetchSyncForBiz(b) {
    const bId = b.businessId || b.business_id || b.id;
    if (!bId) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3000);
    try {
      const syncRes = await ApiBase.get(`/api/sync/status/summary?businessId=${bId}`, { signal: ctrl.signal });
      if (syncRes.ok) {
        const tables = syncRes.data?.data || [];
        const synced = tables.filter(t => t.count > 0);
        const lastSync = synced.length > 0
          ? synced.map(t => t.lastSyncAt).filter(Boolean).sort().reverse()[0]
          : null;
        syncMap[bId] = lastSync;
      }
    } catch (err) {
      syncErrors.push({ bId, msg: err?.name === 'AbortError' ? 'timeout 3s' : (err?.message || 'error') });
    } finally {
      clearTimeout(timer);
    }
  }

  // Concurrencia limitada a 5 para evitar saturar el backend con muchos negocios
  const CONCURRENCY = 5;
  for (let i = 0; i < rows.length; i += CONCURRENCY) {
    await Promise.allSettled(rows.slice(i, i + CONCURRENCY).map(fetchSyncForBiz));
  }

  if (syncErrors.length > 0) {
    console.warn(`[DesktopAdminView] ${syncErrors.length}/${rows.length} negocios fallaron sync status:`, syncErrors);
  }

  // PANEL-OPS-V3-001 T5 — heartbeat KPIs
  const nowMs = Date.now();
  const totEquipos = totalesDeEquipos(equipos);
  const exceden = businesses.filter((b) => equipos.get(b.businessId || b.business_id || b.id)?.excede);
  const lt24 = activeMachines.filter(m => {
    const ls = m.last_seen || m.lastSeen;
    if (!ls) return false;
    const d = new Date(ls);
    return !isNaN(d.getTime()) && (nowMs - d.getTime()) < 86400000;
  }).length;
  const gt72 = activeMachines.filter(m => {
    const ls = m.last_seen || m.lastSeen;
    if (!ls) return true; // sin last_seen se considera stale
    const d = new Date(ls);
    return isNaN(d.getTime()) || (nowMs - d.getTime()) >= 3 * 86400000;
  }).length;

  const heartbeatHtml = `
    <div class="kpi-card-row">
      <div class="kpi-card${totEquipos.exceden ? ' kpi-card--red' : ''}" data-pcs-activos>
        <span class="kpi-card__label">PCs activos</span>
        <span class="kpi-card__value">${totEquipos.pcs}</span>
        <span class="kpi-card__hint">${totEquipos.filas} filas en license_machines${totEquipos.exceden ? ` · ${totEquipos.exceden} negocio${totEquipos.exceden > 1 ? 's' : ''} con PCs de mas` : ''}</span>
      </div>
      <div class="kpi-card kpi-card--green">
        <span class="kpi-card__label">Sincronizan &lt;24h</span>
        <span class="kpi-card__value">${lt24}</span>
        <span class="kpi-card__hint">last_seen reciente</span>
      </div>
      <div class="kpi-card kpi-card--red">
        <span class="kpi-card__label">Sin sincronizar &gt;72h</span>
        <span class="kpi-card__value">${gt72}</span>
        <span class="kpi-card__hint">stale o sin heartbeat</span>
      </div>
    </div>`;

  el.innerHTML = `
    ${heartbeatHtml}
    <div class="da-card">
      <div class="da-card-title" style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:.5rem">
        <span>Negocios con suscripción</span>
        <div style="display:flex;gap:.5rem;flex-wrap:wrap">
          <span class="da-badge da-badge-ok">${registered} con cliente</span>
          <span class="da-badge" style="background:var(--bg-tertiary);color:var(--text-secondary)">${rows.length - registered} sin cliente</span>
          ${expired.length ? `<span class="da-badge da-badge-red">${expired.length} vencida${expired.length>1?'s':''}</span>` : ''}
          ${expiring.length ? `<span class="da-badge da-badge-yellow">${expiring.length} por vencer</span>` : ''}
        </div>
      </div>
      ${expired.length ? `<div style="background:rgba(248,81,73,.1);border:1px solid rgba(248,81,73,.3);border-radius:6px;padding:8px 12px;margin-bottom:8px;font-size:0.875rem;color:var(--accent-red)">
        &#9888; <strong>${expired.length} suscripcion${expired.length>1?'es':''} vencida${expired.length>1?'s':''}:</strong>
        ${expired.map(b => (b.displayName || b.display_name || '')).join(', ')}.
        Los clientes Desktop ven pantalla de bloqueo despues de 5 dias de gracia.
      </div>` : ''}
      ${conflictCount ? `<div style="background:rgba(121,192,255,.1);border:1px solid rgba(121,192,255,.3);border-radius:6px;padding:8px 12px;margin-bottom:8px;font-size:0.875rem;color:var(--accent-blue-light)">
        &#8505; <strong>${conflictCount} negocio${conflictCount>1?'s':''} con PC fisico (fingerprint) compartido.</strong>
        Es legitimo si un mismo dueño/operador administra varios negocios desde el mismo equipo. Revisa la columna "PC Desktop" si necesitas separar instalaciones.
      </div>` : ''}
      ${exceden.length ? `<div data-pcs-de-mas style="background:rgba(248,81,73,.1);border:1px solid rgba(248,81,73,.3);border-radius:6px;padding:8px 12px;margin-bottom:8px;font-size:0.875rem;color:var(--accent-red)">
        &#9888; <strong>${exceden.length} negocio${exceden.length > 1 ? 's' : ''} con mas PCs de los permitidos:</strong>
        ${exceden.map((b) => { const e = equipos.get(b.businessId || b.business_id || b.id); return `${esc(b.displayName || b.display_name || '')} (${e.pcs} de ${e.permitidos})`; }).join(', ')}.
        Un negocio tiene un solo PC salvo la excepcion declarada.
      </div>` : ''}
      ${equiposPublicados ? '' : `<div style="font-size:0.875rem;color:var(--text-secondary);margin-bottom:8px" data-sin-excepciones>
        Los PCs se cuentan por nombre de PC. Las excepciones declaradas (Exito Chino 2) aun no estan publicadas en la API: se marca en rojo todo negocio con mas de un PC.
      </div>`}
      <div class="da-info-banner">
        &#128161; Para registrar o editar el cliente Desktop de un negocio, ve a
        <strong>Negocios</strong> → selecciona el negocio → pestaña <strong>Suscripción</strong>.
      </div>
      <div style="overflow-x:auto">
        <table class="da-table">
          <thead>
            <tr><th>Negocio</th><th>Plan</th><th>PC Desktop</th><th>Version</th><th>Ultima conexion</th><th>Vencimiento</th><th>Sync</th><th>Salud</th><th>Estado</th><th></th></tr>
          </thead>
          <tbody>
            ${rows.map(b => {
              const bId    = b.businessId || b.business_id || b.id;
              const name   = b.displayName || b.display_name || bId;
              const client = getClient(b);
              // PANEL-OPS-V3-001 T5 — license_machines prioriza sobre license_clients.pc_name
              // Fix 2026-04-17: el DTO LicenseMachine expone pc_name/pcName, NO machine_name.
              // Antes se buscaba machine_name (nunca existio) → PC siempre mostraba "No registrado"
              // pese a que BD tenia valor. Corregido a pc_name/pcName como licenseClients.
              const machine = machineByBizId[bId] || null;
              const allMachinesBiz = machinesByBizId[bId] || [];
              const pcFromMachine = machine?.pc_name || machine?.pcName;
              const pcFromClient  = client?.pc_name || client?.pcName || '';
              const pc = pcFromMachine || pcFromClient;
              const pcOrigin = pcFromMachine ? 'machine' : (pcFromClient ? 'client' : '');
              // v2.2.97: si hay multiples machines con fingerprints distintos (ej Tennis Madrid: TENNIS-MADRID + MADRID-C)
              // listar todos los pc_name. Si hay conflicto con otro business (mismo fingerprint), mostrar badge.
              const allPcNames = allMachinesBiz.map(x => x.pc_name || x.pcName).filter(Boolean);
              const extraPcs = allPcNames.length > 1 ? allPcNames.filter(n => n !== pc) : [];
              const conflictOthers = conflictByBizId[bId] || [];
              const conflictNames = conflictOthers.map(oId => {
                const o = businesses.find(x => (x.businessId || x.business_id || x.id) === oId);
                return o?.displayName || o?.display_name || oId;
              });
              const folder = client?.folder_path || client?.folderPath || '';
              const expIso = getEffectiveExp(client);
              const hasClient = !!client;
              const lastSyncIso = syncMap[bId] || null;
              const machineLastSeen = machine?.last_seen || machine?.lastSeen || null;
              const appVer = machine?.app_version || machine?.appVersion || '';
              const eq = equipos.get(bId);
              const pcsBadge = eq ? `<div data-pcs-negocio="${eq.pcs}" style="font-size:0.875rem;margin-top:2px;color:${eq.excede ? 'var(--accent-red)' : 'var(--text-secondary)'}" title="${eq.filas} fila${eq.filas > 1 ? 's' : ''} en license_machines; permitidos ${eq.permitidos}${eq.conExcepcion ? ' (excepcion declarada)' : ''}">${eq.excede ? '&#9888; ' : ''}${eq.pcs} PC${eq.pcs > 1 ? 's' : ''}${eq.excede ? ` de ${eq.permitidos}` : ''}</div>` : '';
              return `
                <tr${eq?.excede ? ' style="background:rgba(248,81,73,.06)"' : ''}>
                  <td><strong>${name}</strong></td>
                  <td><span style="color:var(--text-secondary);font-size:0.875rem">${b.plan || '—'}</span></td>
                  <td>${pcsBadge}${pc ?`<code style="font-size:0.875rem" title="${pcOrigin === 'machine' ? 'license_machines' : 'license_clients (legacy)'}">${pc}</code>${extraPcs.length ? `<div style="font-size:0.875rem;color:var(--text-secondary);margin-top:2px">+ ${extraPcs.map(n => `<code style="font-size:0.875rem">${n}</code>`).join(', ')}</div>` : ''}${conflictOthers.length ? `<div style="font-size:0.875rem;color:var(--accent-blue-light);margin-top:2px" title="Mismo PC fisico (fingerprint) registrado en: ${conflictNames.join(', ')}. Legitimo si un mismo dueño administra varios negocios.">&#8505; PC compartido</div>` : ''}` : '<span style="color:var(--text-muted);font-size:0.875rem">No registrado</span>'}</td>
                  <td style="font-size:0.875rem;color:var(--text-secondary)">${appVer ? `<code style="font-size:0.875rem;background:var(--bg-secondary);padding:2px 6px;border-radius:3px">${appVer}</code>` : '—'}</td>
                  <td style="font-size:0.875rem;color:var(--text-secondary)" title="${machineLastSeen || ''}">${fmtRelativeDA(machineLastSeen)}</td>
                  <td>${expiryChip(expIso, hasClient && !isRealDate(expIso))}</td>
                  <td>${syncBadge(lastSyncIso)}</td>
                  <td>${healthBadge(hasClient, expIso, lastSyncIso)}</td>
                  <td>
                    ${hasClient
                      ? '<span class="da-badge da-badge-ok">Registrado</span>'
                      : '<span class="da-badge" style="background:var(--bg-tertiary);color:var(--text-muted)">Sin cliente</span>'}
                  </td>
                  <td>
                    <a href="#/negocios/${bId}" class="btn-ghost btn-sm" style="font-size:0.875rem;white-space:nowrap">
                      Ir al negocio &#8594;
                    </a>
                  </td>
                </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
      ${rows.length === 0 ? '<p class="empty-state">No hay negocios con suscripción registrados.</p>' : ''}
    </div>`;
}

// ─── Tab 2: Clientes huérfanos (sin negocio vinculado) ────────────────────────

function renderPendientes(el, orphans, businesses, container) {
  if (orphans.length === 0) {
    el.innerHTML = `
      <div class="da-card">
        <div class="da-card-title">Clientes sin negocio vinculado</div>
        <p class="empty-state" style="margin:1.5rem 0">
          ✅ No hay clientes huérfanos. Todos los clientes están vinculados a un negocio.
        </p>
      </div>`;
    return;
  }

  const bizOptions = businesses.map(b => {
    const bId  = b.businessId || b.business_id || b.id;
    const name = b.displayName || b.display_name || bId;
    return `<option value="${bId}">${name}</option>`;
  }).join('');

  el.innerHTML = `
    <div class="da-card">
      <div class="da-card-title">
        Clientes sin negocio vinculado
        <span class="da-badge da-badge-yellow" style="margin-left:.5rem">${orphans.length} pendientes</span>
      </div>
      <p style="color:var(--text-secondary);font-size:0.875rem;margin-bottom:1rem">
        Estos clientes no están vinculados a ningún negocio. Puedes vincularlos para que
        usen la suscripción activa del negocio, o eliminarlos si son duplicados/errores.
      </p>
      <div id="da-orphan-list">
        ${orphans.map((c, i) => `
          <div class="da-orphan-row" id="orphan-${i}">
            <div>
              <div style="font-weight:600">${c.empresa}</div>
              <div style="font-size:0.875rem;color:var(--text-secondary);margin-top:.2rem;display:flex;flex-wrap:wrap;gap:.4rem">
                ${c.pc_name || c.pcName ? `<span>PC: ${c.pc_name || c.pcName}</span>` : ''}
                ${c.folder_path || c.folderPath ? `<span>Carpeta: ${c.folder_path || c.folderPath}</span>` : ''}
                ${c.notes ? `<span style="font-style:italic">${c.notes}</span>` : ''}
                <span>${expiryChip(c.subscription_limit || c.subscriptionLimit)}</span>
              </div>
            </div>
            <div>
              <select class="form-input form-input-sm" id="orphan-sel-${i}">
                <option value="">— Seleccionar negocio —</option>
                ${bizOptions}
              </select>
            </div>
            <div style="display:flex;gap:.4rem;align-items:center;flex-wrap:wrap">
              <button class="btn-primary btn-sm" id="orphan-link-${i}">Vincular</button>
              <button class="btn-ghost btn-sm" id="orphan-del-${i}" style="color:var(--accent-red);border-color:#f8514940" title="Eliminar este registro">Eliminar</button>
              <span class="da-form-status" id="orphan-status-${i}" style="font-size:0.875rem"></span>
            </div>
          </div>`).join('')}
      </div>
    </div>`;

  orphans.forEach((c, i) => {
    // Vincular
    document.getElementById(`orphan-link-${i}`)?.addEventListener('click', async () => {
      const bizId   = document.getElementById(`orphan-sel-${i}`)?.value;
      const $status = document.getElementById(`orphan-status-${i}`);
      if (!bizId) {
        if ($status) { $status.textContent = '⚠ Selecciona un negocio'; $status.style.color = 'var(--accent-red)'; }
        return;
      }
      $status.textContent = 'Vinculando…'; $status.style.color = 'var(--text-secondary)';

      // Enviar TODOS los datos del cliente existente + el nuevo business_id
      // IMPORTANTE: no enviar subscription_limit: null — la API lo requiere como DateTime válido.
      // Preservar el valor existente (puede ser '0001-01-01' si era null en BD, que es válido).
      const r = await LicenseApiClient.upsertClient({
        empresa:            c.empresa,
        pc_name:            c.pcName     || c.pc_name     || null,
        folder_path:        c.folderPath || c.folder_path || null,
        subscription_limit: c.subscriptionLimit || c.subscription_limit || '0001-01-01T00:00:00',
        notes:              c.notes        || null,
        business_id:        bizId,
        active:             true,
      });

      if (r.ok) {
        // Refrescar toda la vista manteniendo el tab "pendientes" activo
        await renderDesktopAdmin(container);
      } else {
        $status.textContent = `Error: ${r.error ?? 'desconocido'}`;
        $status.style.color = 'var(--accent-red)';
      }
    });

    // Eliminar (desactivar)
    document.getElementById(`orphan-del-${i}`)?.addEventListener('click', async () => {
      const $status = document.getElementById(`orphan-status-${i}`);
      if (!await confirmar({ titulo: `Eliminar el registro "${c.empresa}"`, texto: 'Esta acción no se puede deshacer.', aceptar: 'Eliminar registro', peligro: true })) return;
      $status.textContent = 'Eliminando…'; $status.style.color = 'var(--text-secondary)';

      const r = await LicenseApiClient.upsertClient({
        empresa:            c.empresa,
        pc_name:            c.pcName     || c.pc_name     || null,
        folder_path:        c.folderPath || c.folder_path || null,
        subscription_limit: c.subscriptionLimit || c.subscription_limit || '0001-01-01T00:00:00',
        notes:              c.notes || null,
        business_id:        c.businessId || c.business_id || null,
        active:             false,
      });

      if (r.ok) {
        await renderDesktopAdmin(container);
      } else {
        $status.textContent = `Error: ${r.error ?? 'desconocido'}`;
        $status.style.color = 'var(--accent-red)';
      }
    });
  });
}

// ─── Tab 3: Versiones ─────────────────────────────────────────────────────────

async function renderVersions(el) {
  el.innerHTML = '<div class="spinner"></div>';

  const [stableRes, historyRes] = await Promise.all([
    VersionApiClient.getStable(APP),
    VersionApiClient.getHistory(APP, 10),
  ]);

  const stable  = stableRes?.data?.data ?? stableRes?.data ?? null;
  const history = (() => {
    const r = historyRes?.data?.data ?? historyRes?.data ?? [];
    return Array.isArray(r) ? r : [];
  })();

  el.innerHTML = `
    <div class="da-card da-version-stable">
      <div class="da-card-title">Versión estable actual — <code>${APP}</code></div>
      ${stable ? `
        <div class="da-version-big">
          <span class="da-version-num">${stable.version}</span>
          <span class="da-badge da-badge-ok">Stable</span>
        </div>
        <div class="da-version-meta">Publicada: ${fmtDate(stable.publishedAt ?? stable.published_at)}</div>
        ${stable.releaseNotes || stable.release_notes
          ? `<div class="da-version-notes">${stable.releaseNotes ?? stable.release_notes}</div>` : ''}
      ` : '<p class="empty-state">No hay versión estable registrada.</p>'}
    </div>

    <div class="da-card">
      <div class="da-card-title">Publicar nueva versión</div>
      <div class="da-form-grid">
        <label class="da-field">
          <span>Número de versión <span class="da-req">*</span></span>
          <input id="pv-version" class="form-control form-control-sm"
                 placeholder="ej: 1.0.0.903"
                 value="${stable ? incrementPatch(stable.version ?? '') : ''}">
        </label>
        <label class="da-field">
          <span>Marcar como stable</span>
          <select id="pv-stable" class="form-control form-control-sm">
            <option value="true" selected>Sí — publicar como versión estable</option>
            <option value="false">No — solo registrar historial</option>
          </select>
        </label>
        <label class="da-field da-field-full">
          <span>Release notes</span>
          <textarea id="pv-notes" class="form-control form-control-sm" rows="3"
                    placeholder="Describe los cambios de esta versión…"></textarea>
        </label>
      </div>
      <div class="da-form-actions">
        <span id="pv-status" class="da-form-status"></span>
        <button class="btn btn-primary btn-sm" id="pv-publish">Publicar versión</button>
      </div>
    </div>

    <div class="da-card">
      <div class="da-card-title">Historial de versiones</div>
      ${history.length ? `
        <table class="da-table">
          <thead><tr><th>Versión</th><th>Estado</th><th>Publicada</th><th>Release notes</th></tr></thead>
          <tbody>
            ${history.map(v => `
              <tr>
                <td><code>${v.version}</code></td>
                <td>${v.is_stable || v.isStable
                  ? '<span class="da-badge da-badge-ok">Stable</span>'
                  : '<span class="da-badge da-badge-off">—</span>'}</td>
                <td>${fmtDate(v.publishedAt ?? v.published_at)}</td>
                <td style="font-size:0.875rem;color:var(--text-secondary);max-width:300px">${v.releaseNotes ?? v.release_notes ?? ''}</td>
              </tr>`).join('')}
          </tbody>
        </table>
      ` : '<p class="empty-state">Sin historial registrado.</p>'}
    </div>`;

  el.querySelector('#pv-publish')?.addEventListener('click', async () => {
    const version = el.querySelector('#pv-version').value.trim();
    if (!version) { el.querySelector('#pv-status').textContent = '⚠ Versión requerida'; return; }
    const btn = el.querySelector('#pv-publish');
    btn.disabled = true;
    el.querySelector('#pv-status').textContent = 'Publicando…';
    const res = await VersionApiClient.publish({
      appName:      APP,
      version,
      releaseNotes: el.querySelector('#pv-notes').value.trim(),
      isStable:     el.querySelector('#pv-stable').value === 'true',
    });
    if (res.ok) {
      el.querySelector('#pv-status').textContent = `✓ v${version} publicada`;
      el.querySelector('#pv-status').style.color = 'var(--accent-green-text)';
      await renderVersions(el);
    } else {
      el.querySelector('#pv-status').textContent = `Error: ${res.error ?? 'desconocido'}`;
      el.querySelector('#pv-status').style.color = 'var(--accent-red)';
      btn.disabled = false;
    }
  });
}

function incrementPatch(version) {
  const parts = version.split('.');
  if (parts.length < 4) return version;
  parts[3] = String(Number(parts[3]) + 1);
  return parts.join('.');
}

// ─── Tab 4: Alertas ─── PANEL-OPS-V4-001 T3 ───────────────────────────────────
// 4 tipos de alerta (calculados en cliente):
//   1. sync_72h       — maquina sin heartbeat >72h
//   2. gracia          — suscripcion vencida dentro de ventana de gracia (5d)
//   3. exceso_install — negocio con mas instalaciones activas que su plan
//   4. version_vieja  — maquina con version != stable actual
// Severidad: critical (vencida>5d o sin sync>7d), warning (sync>72h, gracia, exceso),
//            info (version vieja).
// Filtros: por tipo + severidad. Export CSV.

const PLAN_INSTALL_LIMITS = {
  'Básico': 1, 'Basico': 1, 'basic': 1,
  'Plus': 2, 'plus': 2,
  'Pro': 3, 'pro': 3,
  'Pro Marketing': 3, 'pro_marketing': 3, 'ProMarketing': 3,
  'Enterprise': 10, 'enterprise': 10,
};

function planLimit(plan) {
  if (!plan) return 1;
  return PLAN_INSTALL_LIMITS[plan] ?? PLAN_INSTALL_LIMITS[String(plan).trim()] ?? 1;
}

function escCsv(v) {
  const s = String(v ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

async function renderAlertas(el, businesses, clientByBizId, clientByEmpresa, activeMachines) {
  el.innerHTML = '<div class="spinner"></div>';

  // Traer version stable para comparar
  let stableVersion = '';
  try {
    const stRes = await VersionApiClient.getStable(APP);
    stableVersion = (stRes?.data?.data ?? stRes?.data)?.version || '';
  } catch { /* no-op */ }

  const nowMs = Date.now();
  const alerts = [];

  // Indexar maquinas por business_id
  const machinesByBiz = {};
  for (const m of activeMachines) {
    const bId = m.business_id || m.businessId;
    if (!bId) continue;
    (machinesByBiz[bId] = machinesByBiz[bId] || []).push(m);
  }

  // Resolver cliente para un negocio
  function getClient(b) {
    const bId = b.businessId || b.business_id || b.id;
    const eKey = autoEmpresaKey(b.displayName || b.display_name || '');
    return clientByBizId[bId] || clientByEmpresa[eKey] || null;
  }

  for (const b of businesses) {
    const bId = b.businessId || b.business_id || b.id;
    const name = b.displayName || b.display_name || bId;
    const plan = b.plan || '—';
    const client = getClient(b);
    const machines = machinesByBiz[bId] || [];

    // 1) sync_72h
    for (const m of machines) {
      const ls = m.last_seen || m.lastSeen;
      if (!ls) {
        alerts.push({
          type: 'sync_72h', severity: 'warning',
          businessId: bId, business: name, plan,
          subject: m.machine_name || m.machineName || '(sin nombre)',
          detail: 'Sin heartbeat registrado',
          value: '—',
        });
        continue;
      }
      const d = new Date(ls);
      if (isNaN(d.getTime())) continue;
      const hours = Math.floor((nowMs - d.getTime()) / 3600000);
      if (hours >= 72) {
        const days = Math.floor(hours / 24);
        alerts.push({
          type: 'sync_72h',
          severity: days >= 7 ? 'critical' : 'warning',
          businessId: bId, business: name, plan,
          subject: m.machine_name || m.machineName || '(sin nombre)',
          detail: `Sin sincronizar hace ${days}d`,
          value: ls,
        });
      }
    }

    // 2) gracia — suscripcion vencida <= 5d
    const expIso = client?.effective_end_date || client?.effectiveEndDate
                || client?.subscription_limit || client?.subscriptionLimit || '';
    if (isRealDate(expIso)) {
      const exp = new Date(expIso);
      const daysOver = Math.floor((nowMs - exp.getTime()) / 86400000);
      if (daysOver >= 0 && daysOver <= 5) {
        alerts.push({
          type: 'gracia', severity: 'warning',
          businessId: bId, business: name, plan,
          subject: 'Suscripcion',
          detail: `Vencida hace ${daysOver}d (gracia hasta 5d)`,
          value: expIso,
        });
      } else if (daysOver > 5) {
        alerts.push({
          type: 'gracia', severity: 'critical',
          businessId: bId, business: name, plan,
          subject: 'Suscripcion',
          detail: `Vencida hace ${daysOver}d (fuera de gracia)`,
          value: expIso,
        });
      }
    }

    // 3) exceso_install — instalaciones activas > limite del plan
    // LIC-UN-PC-POR-NEGOCIO-T1: se cuentan PCs distintos, no filas (Fantasia Tropical eran 12 filas de 1 PC).
    const limit = planLimit(plan);
    const pcs = equiposPorNegocio(machines).get(bId)?.pcs ?? 0;
    if (pcs > limit) {
      alerts.push({
        type: 'exceso_install', severity: 'warning',
        businessId: bId, business: name, plan,
        subject: 'Instalaciones',
        detail: `${pcs} PCs activos / limite ${limit}`,
        value: `${pcs}/${limit}`,
      });
    }

    // 4) version_vieja — maquina con version != stable
    if (stableVersion) {
      for (const m of machines) {
        const v = m.app_version || m.appVersion || m.version || '';
        if (v && v !== stableVersion) {
          alerts.push({
            type: 'version_vieja', severity: 'info',
            businessId: bId, business: name, plan,
            subject: m.machine_name || m.machineName || '(sin nombre)',
            detail: `v${v} (stable: v${stableVersion})`,
            value: v,
          });
        }
      }
    }
  }

  // Contadores por severidad
  const counts = { critical: 0, warning: 0, info: 0 };
  for (const a of alerts) counts[a.severity] = (counts[a.severity] || 0) + 1;

  const TYPE_LABELS = {
    sync_72h: 'Sync >72h',
    gracia: 'Gracia',
    exceso_install: 'Exceso instal.',
    version_vieja: 'Version vieja',
  };

  el.innerHTML = `
    <div class="alert-summary">
      <div class="alert-kpi alert-kpi--critical">
        <span class="alert-kpi__label">Criticas</span>
        <span class="alert-kpi__value">${counts.critical}</span>
      </div>
      <div class="alert-kpi alert-kpi--warning">
        <span class="alert-kpi__label">Warnings</span>
        <span class="alert-kpi__value">${counts.warning}</span>
      </div>
      <div class="alert-kpi alert-kpi--info">
        <span class="alert-kpi__label">Info</span>
        <span class="alert-kpi__value">${counts.info}</span>
      </div>
      <div class="alert-kpi">
        <span class="alert-kpi__label">Total</span>
        <span class="alert-kpi__value">${alerts.length}</span>
      </div>
    </div>

    <div class="da-card">
      <div class="alert-toolbar">
        <div class="alert-filters">
          <label>Tipo
            <select class="form-input form-input-sm" id="alert-f-type">
              <option value="">Todos</option>
              <option value="sync_72h">Sync &gt;72h</option>
              <option value="gracia">Gracia</option>
              <option value="exceso_install">Exceso instal.</option>
              <option value="version_vieja">Version vieja</option>
            </select>
          </label>
          <label>Severidad
            <select class="form-input form-input-sm" id="alert-f-sev">
              <option value="">Todas</option>
              <option value="critical">Critica</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </label>
          <label>Buscar
            <input type="text" class="form-input form-input-sm" id="alert-f-q" placeholder="negocio o detalle" />
          </label>
        </div>
        <button class="btn-ghost btn-sm" id="alert-export-csv">&#128229; Export CSV</button>
      </div>
      ${alerts.length === 0 ? `
        <p class="empty-state" style="margin:1.5rem 0">
          &#10003; Sin alertas. Todo luce saludable.
        </p>
      ` : `
        <div style="overflow-x:auto">
          <table class="da-table alert-table">
            <thead>
              <tr>
                <th>Severidad</th>
                <th>Tipo</th>
                <th>Negocio</th>
                <th>Plan</th>
                <th>Sujeto</th>
                <th>Detalle</th>
                <th></th>
              </tr>
            </thead>
            <tbody id="alert-tbody">
              ${alerts.map((a, i) => alertRow(a, i, TYPE_LABELS)).join('')}
            </tbody>
          </table>
        </div>
      `}
    </div>`;

  function applyFilters() {
    const ft = el.querySelector('#alert-f-type')?.value || '';
    const fs = el.querySelector('#alert-f-sev')?.value || '';
    const fq = (el.querySelector('#alert-f-q')?.value || '').toLowerCase().trim();
    const $body = el.querySelector('#alert-tbody');
    if (!$body) return;
    const filtered = alerts.filter(a => {
      if (ft && a.type !== ft) return false;
      if (fs && a.severity !== fs) return false;
      if (fq) {
        const hay = `${a.business} ${a.subject} ${a.detail}`.toLowerCase();
        if (!hay.includes(fq)) return false;
      }
      return true;
    });
    $body.innerHTML = filtered.length
      ? filtered.map((a, i) => alertRow(a, i, TYPE_LABELS)).join('')
      : `<tr><td colspan="7" style="text-align:center;color:var(--text-secondary);padding:1.5rem">Sin resultados con los filtros aplicados.</td></tr>`;
  }

  el.querySelector('#alert-f-type')?.addEventListener('change', applyFilters);
  el.querySelector('#alert-f-sev')?.addEventListener('change', applyFilters);
  el.querySelector('#alert-f-q')?.addEventListener('input', applyFilters);

  el.querySelector('#alert-export-csv')?.addEventListener('click', () => {
    const ft = el.querySelector('#alert-f-type')?.value || '';
    const fs = el.querySelector('#alert-f-sev')?.value || '';
    const fq = (el.querySelector('#alert-f-q')?.value || '').toLowerCase().trim();
    const rows = alerts.filter(a => {
      if (ft && a.type !== ft) return false;
      if (fs && a.severity !== fs) return false;
      if (fq) {
        const hay = `${a.business} ${a.subject} ${a.detail}`.toLowerCase();
        if (!hay.includes(fq)) return false;
      }
      return true;
    });
    const header = ['severidad','tipo','negocio','plan','sujeto','detalle','valor','business_id'];
    const lines = [header.join(',')];
    for (const a of rows) {
      lines.push([a.severity, a.type, a.business, a.plan, a.subject, a.detail, a.value, a.businessId]
        .map(escCsv).join(','));
    }
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alertas-desktop-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  });
}

function alertRow(a, i, labels) {
  const sevClass = `alert-sev alert-sev--${a.severity}`;
  const sevLabel = a.severity === 'critical' ? 'Critica' : a.severity === 'warning' ? 'Warning' : 'Info';
  return `
    <tr>
      <td><span class="${sevClass}">${sevLabel}</span></td>
      <td><span class="alert-type-chip alert-type-chip--${a.type}">${labels[a.type] || a.type}</span></td>
      <td><strong>${a.business}</strong></td>
      <td style="color:var(--text-secondary);font-size:0.875rem">${a.plan}</td>
      <td>${a.subject}</td>
      <td style="font-size:0.875rem;color:var(--text-heading)">${a.detail}</td>
      <td><a href="#/negocios/${a.businessId}" class="btn-ghost btn-sm" style="font-size:0.875rem">Ir &#8594;</a></td>
    </tr>`;
}
